import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Multi-select for the business's own types — what trade it is in — with a
 * search box, because a long list is unusable as a native <select multiple>.
 *
 * Not to be confused with the service or product category pickers: those group
 * one business's own catalogue, this says what the business *is*.
 */
export default function BusinessTypePicker({ businessTypes, selected, onChange, error }) {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");

  const visible = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return businessTypes;
    return businessTypes.filter((type) => type.name.toLowerCase().includes(needle));
  }, [businessTypes, term]);

  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const chosen = businessTypes.filter((type) => selected.includes(type.id));

  return (
    // The id is a focus target: OUP-2 sends a failed submit here when no type
    // was chosen, and this control has no single input to land on.
    <div id="businessTypeIds" tabIndex={-1}>
      <span className="field-label">
        {t("businessTypes.label")} <span className="text-danger-text">*</span>
      </span>

      {chosen.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {chosen.map((type) => (
            <span
              key={type.id}
              className="inline-flex items-center gap-1 rounded-full bg-accent-wash py-1 ps-3 pe-1 text-xs font-medium text-accent-text"
            >
              {type.name}
              <button
                type="button"
                onClick={() => toggle(type.id)}
                aria-label={`${t("common.remove")} ${type.name}`}
                className="grid h-4 w-4 place-items-center rounded-full hover:bg-accent hover:text-accent-on"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={`rounded-lg border ${error ? "border-danger" : "border-line-strong"}`}>
        <input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t("businessTypes.search")}
          className="w-full rounded-t-lg border-b border-line px-3 py-2 text-sm outline-none"
        />

        <div className="max-h-44 overflow-y-auto p-1">
          {businessTypes.length === 0 && (
            <p className="px-2 py-3 text-sm text-ink-2">
              {t("businessTypes.none")}
            </p>
          )}

          {businessTypes.length > 0 && visible.length === 0 && (
            <p className="px-2 py-3 text-sm text-ink-2">{t("common.noMatches")}</p>
          )}

          {visible.map((type) => (
            <label
              key={type.id}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-raised"
            >
              <input
                type="checkbox"
                checked={selected.includes(type.id)}
                onChange={() => toggle(type.id)}
                className="h-4 w-4 rounded border-line-strong"
              />
              {type.name}
            </label>
          ))}
        </div>
      </div>

      {error && <p className="field-error">{error}</p>}
    </div>
  );
}
